import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Drawer latéral (slide-in from right) - pattern Stripe/Linear pour les
 * panneaux de filtres ou de configuration. Ferme sur :
 *  - touche Échap
 *  - clic sur la croix
 *
 * **Non bloquant** : ne recouvre PAS la page derrière lui. L'utilisateur
 * peut continuer à scroller / cliquer / survoler la liste pendant que le
 * drawer est ouvert - utile pour voir l'effet des filtres en live. La
 * conséquence : pas de fermeture par click-outside (on évite de la
 * déclencher par mégarde en voulant simplement interagir avec la page).
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Largeur du drawer. `sm` = 320px, `md` = 420px, `lg` = 560px. */
  width?: 'sm' | 'md' | 'lg';
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const widthClass = width === 'sm' ? 'w-[320px]' : width === 'lg' ? 'w-[560px]' : 'w-[420px]';

  return (
    // Panneau positionné à droite, sans overlay ni wrapper `inset-0` : la
    // page derrière reste pleinement interactive (scroll, hover, clic sur
    // les lignes). Séparation visuelle via `shadow-modal` + border.
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={title}
      className={cn(
        'fixed right-0 top-0 bottom-0 z-50 flex flex-col border-l border-border-subtle bg-white shadow-modal animate-slide-in-from-right',
        widthClass,
      )}
    >
      <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
        <h2 className="text-base font-semibold tracking-tight text-brand">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="flex h-8 w-8 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-gray-100 hover:text-brand"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <div className="border-t border-border-subtle bg-gray-50 px-5 py-3">{footer}</div>}
    </div>
  );
}
